"use client";

import { useEffect, useMemo, useRef, useState } from "react";

/**
 * The e-signature panel that appears once someone ticks "I agree".
 *
 * WHY A TYPED NAME AND NOT JUST THE CHECKBOX
 * ------------------------------------------
 * A checkbox is an ambiguous act — it could be a misclick, and it carries no
 * evidence of who did it. Typing your own name is deliberate, and E-SIGN
 * (15 U.S.C. § 7001) / UETA both turn on demonstrable INTENT to sign. Our Terms
 * already claim E-SIGN protection, so the signing act should look like one.
 *
 * ⚠️ THE CURSIVE IS PRESENTATION, NOT PROTECTION. What actually defends this is
 * the record written server-side: the typed name, the timestamp, the IP, and a
 * hash of the exact Terms text shown. This component's job is to make the act
 * deliberate and legible; TermsSignature must never be the only artefact.
 *
 * Pre-filled from the Full Name field because that is the name on the account —
 * a signature that disagrees with it is worth noticing, so the mismatch is
 * surfaced rather than silently corrected.
 */
export function TermsSignature({
  accountName,
  value,
  onChange,
  disabled,
}: {
  /** The Full Name typed earlier in the form. */
  accountName: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  const [touched, setTouched] = useState(false);
  const seeded = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // ⚠️ SET THE SIZE IMPERATIVELY — a class cannot win here.
  //
  // globals.css carries `input[type="text"] { font-size: 16px !important }` to
  // stop WKWebView auto-zooming the page when a small input takes focus. That
  // selector is (0,1,1) and important, so it beats any utility class, including
  // Tailwind's `!` variants — all three were measured rendering at 16px.
  //
  // Overriding UPWARD is safe and keeps that rule's intent: the 16px is a floor
  // against zoom, and a larger signature is still above the floor. Do not
  // "simplify" this back to a className.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.setProperty('font-size', '2.1rem', 'important');

    // SIT THE NAME ON THE RULE, AND STOP CLIPPING THE FIRST LETTER.
    //
    // Two separate problems, both measured rather than eyeballed:
    //
    // 1. The default line box was 42px around a ~26px glyph, so 8px of
    //    half-leading sat under the letters, plus 8px of padding — 16px of air
    //    between the signature and the rule it is meant to rest on. line-height
    //    1 collapses the box to the glyph; 3px of padding leaves just enough for
    //    descenders (g, y) to cross the rule the way a real signature does.
    //
    // 2. Script capitals carry a swash that starts LEFT of the glyph origin, so
    //    4px of padding clipped the first letter. 12px clears it.
    el.style.setProperty('line-height', '1');
    el.style.setProperty('padding-left', '12px');
    el.style.setProperty('padding-bottom', '3px');
  }, []);

  // Seed from the account name ONCE. Re-seeding on every keystroke would fight
  // anyone correcting their own signature (middle name, different spelling).
  useEffect(() => {
    if (seeded.current) return;
    if (!value && accountName.trim()) {
      onChange(accountName.trim());
      seeded.current = true;
    }
  }, [accountName, value, onChange]);

  const signedOn = useMemo(
    () =>
      new Date().toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
    [],
  );

  const differs =
    touched &&
    value.trim().length > 0 &&
    accountName.trim().length > 0 &&
    value.trim().toLowerCase() !== accountName.trim().toLowerCase();

  return (
    <div className="mt-3 rounded-lg border border-gray-300 bg-gradient-to-b from-white to-gray-50/60 p-4">
      <p className="mb-3 text-xs leading-relaxed text-gray-600">
        Type your full legal name below. This is your electronic signature and has the same
        legal effect as signing on paper.
      </p>

      <label htmlFor="terms-signature" className="sr-only">
        Electronic signature — your full legal name
      </label>
      <input
        ref={inputRef}
        id="terms-signature"
        type="text"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => setTouched(true)}
        autoComplete="off"
        spellCheck={false}
        placeholder={accountName || "Your full legal name"}
        // --font-signature is Great Vibes, self-hosted via next/font (see
        // app/layout.tsx). The cursive stack behind it is the fallback: if the
        // face ever fails to load this degrades to *a* script font rather than
        // to plain sans, which would silently strip the signature affordance.
        style={{
          fontFamily:
            'var(--font-signature), "Snell Roundhand", "Apple Chancery", "Segoe Script", cursive',
        }}
        className="w-full border-0 border-b-2 border-gray-400 bg-transparent leading-none text-gray-900 focus:border-primary focus:outline-none focus:ring-0 disabled:opacity-60"
      />

      <div className="mt-2 flex items-end justify-between gap-4">
        <span className="text-[11px] uppercase tracking-wider text-gray-400">
          Electronic signature
        </span>
        <span className="text-[11px] text-gray-500">Signed {signedOn}</span>
      </div>

      {differs && (
        <p className="mt-2 text-[11px] text-amber-700">
          This differs from the name on your account ({accountName.trim()}). That is allowed —
          just make sure it is your legal name.
        </p>
      )}
    </div>
  );
}

export default TermsSignature;
